import { useEffect, useRef, useState, useCallback } from "react";
import { createTimeline, onScroll } from "animejs";
import TimelineEntry from "./TimelineEntry";
import useReducedMotion from "../../hooks/useReducedMotion";

export default function Timeline({ entries }) {
	const outerRef = useRef(null);
	const rootRef = useRef(null);
	const spineRef = useRef(null);
	const spineHRef = useRef(null);
	const reduced = useReducedMotion();
	const [activeIndex, setActiveIndex] = useState(0);

	const scrollToEntry = useCallback((i) => {
		const root = rootRef.current;
		if (!root) return;
		const el = root.querySelectorAll(".tl-entry")[i];
		if (!el) return;
		// scrollIntoView resolves centering against the actual rendered layout,
		// bypassing any snap-engine interference from scroll-snap-type.
		el.scrollIntoView({
			behavior: "smooth",
			inline: "center",
			block: "nearest",
		});
		setActiveIndex(i);
	}, []);

	useEffect(() => {
		const spine = spineRef.current;
		const spineH = spineHRef.current;
		const root = rootRef.current;
		if (!spine || !root) return;

		const isHorizontal = window.matchMedia("(min-width: 801px)").matches;

		if (isHorizontal && spineH) {
			if (!reduced) {
				spineH.style.transformOrigin = "right center";
				spineH.style.transform = "scaleX(1)";
			}

			// Set list padding so every entry — including first and last —
			// can be scrolled to exact centre of the viewport.
			const list = root.querySelector(".tl-list");
			const syncPadding = () => {
				const entries = root.querySelectorAll(".tl-entry");
				const startSpacer = list?.querySelector(".tl-spacer--start");
				const endSpacer = list?.querySelector(".tl-spacer--end");
				if (!list || !entries.length || !startSpacer || !endSpacer) return;
				const first = entries[0];
				// Spacer elements are real flex items so their width is always
				// counted in scrollWidth — unlike padding-right or margin-inline-end
				// which browsers handle inconsistently on overflow flex containers.
				const offset = Math.max(0, (root.clientWidth - first.offsetWidth) / 2);
				startSpacer.style.width = `${offset}px`;
				endSpacer.style.width = `${offset}px`;
			};
			syncPadding();
			const padRO = new ResizeObserver(syncPadding);
			padRO.observe(root);

			const onHScroll = () => {
				const max = root.scrollWidth - root.clientWidth;
				if (!max) return;

				const t = Math.max(0, 1 - root.scrollLeft / max);
				spineH.style.transform = `scaleX(${t})`;

				const centre = root.scrollLeft + root.clientWidth / 2;
				const els = root.querySelectorAll(".tl-entry");
				let bestIdx = 0;
				let bestDist = Infinity;
				els.forEach((el, i) => {
					const dist = Math.abs(
						el.offsetLeft + el.offsetWidth / 2 - centre,
					);
					if (dist < bestDist) {
						bestDist = dist;
						bestIdx = i;
					}
				});
				setActiveIndex((prev) => (prev === bestIdx ? prev : bestIdx));
			};
			root.addEventListener("scroll", onHScroll, { passive: true });

			const onWheel = (e) => {
				if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
				e.preventDefault();
				let delta = e.deltaY;
				if (e.deltaMode === 1) delta *= 40;
				else if (e.deltaMode === 2) delta *= root.clientWidth;
				delta = Math.sign(delta) * Math.min(Math.abs(delta), 200);
				root.scrollLeft += delta * 1.5;
			};
			root.addEventListener("wheel", onWheel, { passive: false });

			return () => {
				padRO.disconnect();
				root.removeEventListener("scroll", onHScroll);
				root.removeEventListener("wheel", onWheel);
			};
		}

		// ---- Vertical spine (mobile) ----
		const length = spine.getTotalLength();
		spine.style.strokeDasharray = `${length}`;
		spine.style.strokeDashoffset = reduced ? "0" : `${length}`;
		if (reduced) return;

		let scrollObserver;
		try {
			scrollObserver = onScroll({
				container: window,
				target: root,
				enter: "top center",
				leave: "bottom center",
				sync: 0.4,
			});
		} catch (_) {
			scrollObserver = undefined;
		}

		const tl = createTimeline({
			autoplay: scrollObserver ?? true,
			defaults: { ease: "linear" },
		});
		tl.add(spine, { strokeDashoffset: [length, 0], duration: 1000 });

		return () => tl.pause?.();
	}, [reduced]);

	return (
		<>
			<div className="tl-outer" ref={outerRef}>
				<div className="tl-spine-h" ref={spineHRef} aria-hidden="true" />
				<div className="tl-root" ref={rootRef}>
					<svg
						className="tl-spine"
						aria-hidden="true"
						preserveAspectRatio="none"
					>
						<line
							ref={spineRef}
							x1="50%"
							y1="0"
							x2="50%"
							y2="100%"
							stroke="var(--rule-color)"
							strokeWidth="2"
						/>
					</svg>
					<ol className="tl-list">
						<li className="tl-spacer tl-spacer--start" aria-hidden="true" />
						{entries.map((entry, i) => (
							<TimelineEntry
								key={entry.id}
								entry={entry}
								index={i}
								side={i % 2 === 0 ? "left" : "right"}
							/>
						))}
						<li className="tl-spacer tl-spacer--end" aria-hidden="true" />
					</ol>
				</div>

				{/* Floats inside tl-outer so it stays within the timeline frame */}
				<nav className="tl-progress" aria-label="Timeline navigation">
					{entries.map((entry, i) => (
						<button
							key={entry.id}
							className={`tl-progress__step${activeIndex === i ? " tl-progress__step--active" : ""}`}
							onClick={() => scrollToEntry(i)}
							aria-label={entry.org}
							aria-current={activeIndex === i ? "true" : undefined}
						>
							<span className="tl-progress__dot" />
						</button>
					))}
				</nav>
			</div>
		</>
	);
}
